import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Grid3x3, FileText, ZoomIn, Layers, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";

const STEPS = [
  {
    icon: FileText,
    kicker: "01 — Welcome",
    title: "See the Big Picture by Mastering the Tiny Details.",
    sub: "Stop drowning in linear paragraphs. Transform your class notes into an interactive, infinite 3×3 concept map.",
    cta: "Get Started",
  },
  {
    icon: Grid3x3,
    kicker: "02 — The Core Grid",
    title: "The Power of the Center",
    sub: "Type a topic or upload lecture notes into the center cell. Our AI instantly extracts the 8 most critical sub-topics around your core concept.",
    cta: "Next",
  },
  {
    icon: ZoomIn,
    kicker: "03 — Infinite Zoom",
    title: "Drill Down Indefinitely",
    sub: "Click any outer cell and it glides into the center, instantly generating 8 even deeper layers of detail — up to 6 layers deep.",
    cta: "Next",
  },
  {
    icon: Layers,
    kicker: "04 — Study & Export",
    title: "Study Smarter, Sync Anywhere",
    sub: "The AI quietly builds high-yield flashcards as you explore. Study them in the quick viewer, or export your master tree to Anki in one click.",
    cta: "Create Your First Grid",
  },
];

function MiniGrid({ step }) {
  return (
    <div className="relative grid grid-cols-3 grid-rows-3 gap-2 w-64 h-64">
      {Array.from({ length: 9 }).map((_, i) => {
        const isCenter = i === 4;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: isCenter ? 1.08 : 1 }}
            transition={{ delay: i * 0.05, type: "spring", stiffness: 200 }}
            className={
              "rounded-xl flex items-center justify-center " +
              (isCenter
                ? "bg-gradient-to-br from-purple-700/60 to-purple-950/80 border-2 border-purple-400 center-pulse"
                : "bg-purple-500/5 border border-purple-500/20")
            }
          >
            {isCenter ? (
              <Sparkles className="w-7 h-7 text-purple-300" />
            ) : (
              step === 1 && <div className="w-2 h-2 rounded-full bg-purple-400/60" />
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

export default function Onboarding({ onFinish }) {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const Icon = step.icon;
  const next = () => (i < STEPS.length - 1 ? setI(i + 1) : onFinish());

  return (
    <div className="app-bg min-h-screen flex flex-col items-center justify-center px-6 py-12" data-testid="onboarding-screen">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-12 items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.4 }}
          >
            <span className="font-mono-fx text-xs uppercase tracking-widest text-purple-400">{step.kicker}</span>
            <div className="mt-4 mb-5 inline-flex p-3 rounded-2xl bg-purple-500/10 border border-purple-500/25">
              <Icon className="w-6 h-6 text-purple-300" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white glow-text leading-[1.05]">
              {step.title}
            </h1>
            <p className="mt-5 text-base text-purple-200/80 leading-relaxed max-w-md">{step.sub}</p>

            <div className="mt-8 flex items-center gap-4">
              <Button
                data-testid="onboarding-next-btn"
                onClick={next}
                className="rounded-full bg-purple-600 hover:bg-purple-500 text-white px-7 h-12 text-base font-semibold shadow-[0_0_24px_rgba(168,85,247,0.5)]"
              >
                {step.cta} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              {i < STEPS.length - 1 && (
                <button
                  data-testid="onboarding-skip-btn"
                  onClick={onFinish}
                  className="text-sm text-purple-300/60 hover:text-purple-200 transition-colors"
                >
                  Skip intro
                </button>
              )}
            </div>

            <div className="mt-10 flex gap-2">
              {STEPS.map((_, idx) => (
                <div
                  key={idx}
                  className={"h-1.5 rounded-full transition-all duration-300 " + (idx === i ? "w-8 bg-purple-400" : "w-2 bg-purple-500/25")}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="hidden lg:flex justify-center">
          <div className="relative p-8 rounded-3xl bg-surface-card/40 border border-purple-500/15 grid-noise overflow-hidden">
            <MiniGrid step={i} />
          </div>
        </div>
      </div>
    </div>
  );
}
